import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MessageBox from '../components/MessageBox'
import { fetchWithAuth } from '../utils/fetchWithAuth'

const icons = {
  admin: require('../assets/images/icons/admin.png'),
  personal: require('../assets/images/icons/registration.png'),
  subscriptions: require('../assets/images/icons/subscription.png'),
  receipts: require('../assets/images/icons/fuel.png'),
  wallet: require('../assets/images/icons/wallet.png'),
  network: require('../assets/images/icons/network.png'),
  ledger: require('../assets/images/icons/ledger.png'),
  fundsAllocation: require('../assets/images/icons/funds_allocation.png'),
  cashback: require('../assets/images/icons/cashback.png'),
  dashboard: require('../assets/images/icons/dashboard.png'),
  matomo: require('../assets/images/icons/matomo.png'),
  script: require('../assets/images/icons/script.png'),
}

const adminButtons = [
  {
    label: 'Systémové nastavenia',
    icon: icons.admin,
    action: 'SYSTEM_SETTINGS',
  },
  {
    label: 'Notifikačné scripty',
    icon: icons.script,
    action: 'NOTIFICATION_SCRIPTS',
  },
  {
    label: 'Admin scripty',
    icon: icons.script,
    iconFilter:
      'brightness(0) saturate(100%) invert(16%) sepia(99%) saturate(6065%) hue-rotate(355deg) brightness(91%) contrast(119%)',
    action: 'ADMIN_SCRIPTS',
  },
  {
    label: 'Testovacie scripty',
    icon: icons.script,
    iconFilter:
      'brightness(0) saturate(100%) invert(38%) sepia(61%) saturate(752%) hue-rotate(126deg) brightness(89%) contrast(95%)',
    action: 'TEST_SCRIPTS',
  },
  {
    label: 'Zoznam účtov',
    icon: icons.personal,
    centerView: 'ADMIN_USERS',
  },
  {
    label: 'Predplatné - zoznam',
    icon: icons.subscriptions,
    centerView: 'ADMIN_SUBSCRIPTIONS',
  },
  {
    label: 'Zoznam účteniek',
    icon: icons.receipts,
    centerView: 'ADMIN_RECEIPTS',
  },
  {
    label: 'Peňaženky',
    icon: icons.wallet,
    centerView: 'ADMIN_WALLET',
  },
  {
    label: 'Sieť',
    icon: icons.network,
    centerView: 'ADMIN_NETWORK_VIZ',
  },
  {
    label: 'Účtovná kniha',
    icon: icons.ledger,
    centerView: 'ADMIN_LEDGER',
  },
  {
    label: 'Účtovné alokácie',
    icon: icons.fundsAllocation,
    centerView: 'ADMIN_PAYMENT_ALLOCATIONS',
  },
  {
    label: 'Cashback Lab',
    icon: icons.cashback,
    centerView: 'ADMIN_CASHBACK_LAB',
  },
  {
    label: 'Audit logy',
    icon: icons.dashboard,
    centerView: 'ADMIN_AUDIT_LOGS',
  },
  {
    label: 'Matomo',
    icon: icons.matomo,
    externalUrl: 'https://analytics.enerqiq.net/',
  },
]

const systemSettingsButtons = [
  {
    label: 'Predplatné',
    centerView: 'ADMIN_MEMBERSHIP_TYPES',
  },
  {
    label: 'MLM bonusy',
    centerView: 'ADMIN_MLM_BONUS_LEVELS',
  },
  {
    label: 'Poisťovne',
    centerView: 'ADMIN_INSURANCE',
  },
  {
    label: 'Promobannery',
    centerView: 'ADMIN_PROMO_BANNERS',
  },
  {
    label: 'Krajiny registrácie',
    centerView: 'ADMIN_COUNTRIES',
  },  
  {
    label: 'Systémové parametre',
    centerView: 'ADMIN_SYSTEM_PARAMETERS',
  },
]

const notificationScriptButtons = [
  {
    label: 'Pripomienka TK/EK',
    script: 'stk-reminders',
    tooltip:
      'Spustí kontrolu termínov STK a EK a odošle upozornenia',
  },
  {
    label: 'Sezónne prezúvanie',
    script: 'tirechange-reminders',
    tooltip:
      'Odošle upozornenie na termín prezúvania pneu (zimné/letné)',
  },
  {
    label: 'Servisná prehliadka',
    script: 'service-reminders',
    tooltip:
      'Odošle upozornenie na termín servisnej prehliadky',
  },
  {
    label: 'Poistné',
    script: 'insurance-reminders',
    tooltip:
      'Odošle upozornenie na termín PZP, alebo havarijnej poistky.',
  },
  {
    label: 'Predplatné',
    script: 'subscription-reminders',
    tooltip:
      'Odošle upozornenie na termín konca predplatného',
  },
  {
    label: 'Účet',
    script: 'userdelete-reminders',
    tooltip:
      'Upozornenie na vymazanie účtu po nezakúpení predplatného 30 dní od registrácie',
  },
]

const adminScriptButtons = [
  {
    label: 'Cashback kalkulácia',
    script: 'cashback',
    requiresMonth: true,
    requiresMfa: true,
  },
  {
    label: 'Deaktivácia neaktívnych účtov',
    script: 'deactivateRegisteredUsers',
    requiresMfa: true,
    tooltip:
      'Deaktivuje členov, ktorí si po registrácii do 30 dní nekúpili predplatné',
  },
  {
    label: 'Deaktivácia expirovaných účtov',
    script: 'deactivateUsers',
    requiresMfa: true,
    tooltip:
      'Deaktivuje členov, ktorí si 30 dní po expirácii neobnovili predplatné',
  },
  {
    label: 'Rebalance siete',
    script: 'networkRebalance',
    requiresMfa: true,
    tooltip:
      'Reorganizuje sieť po vymazaní deaktivovaných členov',
  },
  {
    label: 'Rebalance revert',
    script: 'networkRebalanceRevert',
    requiresMfa: true,
    tooltip:
      'Obnoví sieť k stavu pred poslednou reorganizáciou',
  },
]

const testScriptButtons = [
  {
    label: 'Payout export',
    script: 'exportPayouts',
    successMessage: 'Exported',
    tooltip:
      'Vytvorí JSON pre výbery a odošle ich do banky',
  },
  {
    label: 'Bank response',
    script: 'simulateBank',
    successMessage: 'Bank responded',
    tooltip:
      'Vytvorí JSON, ako keby odpoveď z banky na odoslané výbery',
  },
  {
    label: 'Webhook',
    script: 'simulateWebhook',
    successMessage: 'Webhook completed',
    tooltip:
      'Simuluje spracovanie výberov v banke',
  },
]

const AdminMenu = ({ layout, setCenterView }) => {
  const navigate = useNavigate()

  const [openSubmenu, setOpenSubmenu] = useState(null)
  const [message, setMessage] = useState(null)
  const [scriptResult, setScriptResult] = useState(null)
  const [runningScript, setRunningScript] = useState(null)
  const [landingPageLocked, setLandingPageLocked] = useState(false)
  const [landingPageLockLoading, setLandingPageLockLoading] = useState(true)

  const [tooltip, setTooltip] = useState(null)
  const [tooltipPosition, setTooltipPosition] = useState({
    x: 0,
    y: 0,
  })

  const tooltipDelayTimerRef = useRef(null)
  const tooltipHideTimerRef = useRef(null)

  const clearTooltipTimers = () => {
    if (tooltipDelayTimerRef.current) {
      clearTimeout(tooltipDelayTimerRef.current)
      tooltipDelayTimerRef.current = null
    }

    if (tooltipHideTimerRef.current) {
      clearTimeout(tooltipHideTimerRef.current)
      tooltipHideTimerRef.current = null
    }
  }

  const hideTooltip = () => {
    clearTooltipTimers()
    setTooltip(null)
  }

  useEffect(() => {
    return () => {
      clearTooltipTimers()
    }
  }, [])

  useEffect(() => {
    const loadLandingPageLock = async () => {
      try {
        const response = await fetchWithAuth('/admin/company')
        const data = await response.json()

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Stav blokovania stránky sa nepodarilo načítať.'
          )
        }

        setLandingPageLocked(
          data?.landing_page_locked === true
        )
      } catch (error) {
        setMessage({
          message:
            error?.message ||
            'Stav blokovania stránky sa nepodarilo načítať.',
          type: 'error',
        })
      } finally {
        setLandingPageLockLoading(false)
      }
    }

    loadLandingPageLock()
  }, [])

  const showTooltip = (text, event) => {
    if (!text) return

    clearTooltipTimers()
    setTooltip(null)

    setTooltipPosition({
      x: event.clientX,
      y: event.clientY,
    })

    tooltipDelayTimerRef.current = setTimeout(() => {
      setTooltip(text)

      tooltipHideTimerRef.current = setTimeout(() => {
        setTooltip(null)
      }, 5000)
    }, 600)
  }

  const updateTooltipPosition = (event) => {
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY,
    })
  }

  const toggleSubmenu = (submenuName) => {
    setOpenSubmenu((current) =>
      current === submenuName
        ? null
        : submenuName
    )
  }

const handleAdminButtonClick = (item) => {
  if (item.action) {
    toggleSubmenu(item.action)
    return
  }

  setOpenSubmenu(null)
  hideTooltip()

  if (item.centerView) {
    setCenterView(item.centerView)
    return
  }

  if (item.externalUrl) {
    window.open(
      item.externalUrl,
      '_blank',
      'noopener,noreferrer'
    )
    return
  }

  if (item.route) {
    navigate(item.route)
  }
}

  const handleSystemSettingsClick = (item) => {
    setOpenSubmenu(null)
    hideTooltip()

    if (item.centerView) {
      setCenterView(item.centerView)
      return
    }

    if (item.route) {
      navigate(item.route)
    }
  }

  const fromServerMessage = (data) => {
    const raw = data?.message || ''

    if (/^UNKNOWN_MESSAGE$/i.test(raw)) {
      return 'Unknown message'
    }

    const lines = raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)

    return lines.length
      ? lines[lines.length - 1]
      : raw
  }

  const requestAdminMfaCode = () => {
    const code = window.prompt(
      'Zadaj 6-miestny MFA kód z autentifikačnej aplikácie'
    )

    if (!code) return null

    return code.trim()
  }

  const runNotificationScript = async (item) => {
    if (runningScript) return

    hideTooltip()
    setRunningScript(item.script)
    setScriptResult(null)

    try {
      const response = await fetchWithAuth(
        '/admin/run-script',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            script: item.script,
          }),
        }
      )

      const data = await response.json()

      setScriptResult({
        message: fromServerMessage(data),
        type: response.ok
          ? 'success'
          : 'error',
      })
    } catch (error) {
      setScriptResult({
        message:
          error?.message ||
          'Notifikačný script sa nepodarilo spustiť.',
        type: 'error',
      })
    } finally {
      setRunningScript(null)
    }
  }

  const runAdminScript = async (item) => {
    if (runningScript) return

    let month

    if (item.requiresMonth) {
      month = window.prompt(
        'Zadaj mesiac vo formáte YYYY-MM',
        new Date().toISOString().slice(0, 7)
      )

      if (!month) return
    }

    let mfaCode

    if (item.requiresMfa) {
      mfaCode = requestAdminMfaCode()

      if (!mfaCode) return
    }

    hideTooltip()
    setRunningScript(item.script)
    setScriptResult(null)

    try {
      const payload = {
        script: item.script,
        mfaCode,
      }

      if (month) {
        payload.month = month
      }

      const response = await fetchWithAuth(
        '/admin/run-script',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      )

      const data = await response.json()

      setScriptResult({
        message: fromServerMessage(data),
        type: response.ok
          ? 'success'
          : 'error',
      })
    } catch (error) {
      setScriptResult({
        message:
          error?.message ||
          'Admin script sa nepodarilo spustiť.',
        type: 'error',
      })
    } finally {
      setRunningScript(null)
    }
  }

  const runTestScript = async (item) => {
    if (runningScript) return

    hideTooltip()
    setRunningScript(item.script)
    setScriptResult(null)

    try {
      const response = await fetchWithAuth(
        '/admin/run-script',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            script: item.script,
          }),
        }
      )

      if (response.ok) {
        setScriptResult({
          message: item.successMessage,
          type: 'success',
        })

        return
      }

      const data = await response.json()

      setScriptResult({
        message: fromServerMessage(data),
        type: 'error',
      })
    } catch (error) {
      setScriptResult({
        message:
          error?.message ||
          'Testovací script sa nepodarilo spustiť.',
        type: 'error',
      })
    } finally {
      setRunningScript(null)
    }
  }

  const toggleLandingPageLock = async () => {
    if (landingPageLockLoading) return

    const locked = !landingPageLocked

    setLandingPageLockLoading(true)
    setMessage(null)

    try {
      const response = await fetchWithAuth(
        '/admin/company/landing-page-lock',
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            locked
          }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data?.message ||
            'Stav blokovania stránky sa nepodarilo zmeniť.'
        )
      }

      setLandingPageLocked(
        data.locked === true
      )
    } catch (error) {
      setMessage({
        message:
          error?.message ||
          'Stav blokovania stránky sa nepodarilo zmeniť.',
        type: 'error',
      })
    } finally {
      setLandingPageLockLoading(false)
    }
  }

  const renderSubmenuButton = ({
    key,
    label,
    onClick,
    tooltipText,
    disabled = false,
    lightRed = false,
  }) => (
    <button
      key={key}
      type="button"
      disabled={disabled}
      data-promo-clickable="true"
      onClick={onClick}
      style={{
        ...submenuButton,
        width: layout.boxWidth + 20,
        height: Math.max(
          layout.boxHeight - 8,
          28
        ),
        fontSize: Math.max(
          layout.fontSize - 1,
          12
        ),
        opacity: disabled ? 0.65 : 1,
        cursor: disabled
          ? 'wait'
          : 'pointer',
        background: lightRed
          ? '#f3d6d6'
          : '#ededed',
        color: lightRed
          ? '#7a1f1f'
          : '#0f3f39',
        borderColor: lightRed
          ? '#c96b6b'
          : '#107c6b',
      }}
      onMouseEnter={(event) => {
        if (!disabled) {
          event.currentTarget.style.background =
            lightRed
              ? '#edbcbc'
              : 'rgba(16,124,107,0.30)'

          event.currentTarget.style.boxShadow =
            '0 6px 16px rgba(0,0,0,0.35)'
        }

        showTooltip(
          tooltipText,
          event
        )
      }}
      onMouseMove={(event) => {
        if (tooltipText) {
          updateTooltipPosition(event)
        }
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background =
          lightRed
            ? '#f3d6d6'
            : '#ededed'

        event.currentTarget.style.boxShadow =
          '0 4px 12px rgba(0,0,0,0.25)'

        event.currentTarget.style.transform =
          'scale(1)'

        hideTooltip()
      }}
      onMouseDown={(event) => {
        if (disabled) return

        event.currentTarget.style.transform =
          'scale(0.96)'

        event.currentTarget.style.boxShadow =
          '0 2px 6px rgba(0,0,0,0.2)'
      }}
      onMouseUp={(event) => {
        if (disabled) return

        event.currentTarget.style.transform =
          'scale(1)'

        event.currentTarget.style.boxShadow =
          '0 6px 16px rgba(0,0,0,0.35)'
      }}
    >
      {label}
    </button>
  )

  const submenuTop = (buttonIndex) =>
    buttonIndex * (
      layout.boxHeight + 6
    )

  const renderSubmenuConnector = (
    buttonIndex
  ) => (
    <div
      style={{
        ...submenuConnector,
        left: layout.boxWidth,
        top:
          submenuTop(buttonIndex) +
          layout.boxHeight / 2,
      }}
    />
  )

  return (
    <>
      {message && (
        <div style={messageWrapper}>
          <MessageBox
            message={message.message}
            type={message.type}
          />
        </div>
      )}

      {scriptResult && (
        <div style={scriptResultOverlay}>
          <div style={scriptResultModal}>
            <button
              type="button"
              onClick={() => setScriptResult(null)}
              aria-label="Zavrieť"
              style={scriptResultCloseButton}
            >
              ×
            </button>

            <MessageBox
              message={scriptResult.message}
              type={scriptResult.type}
            />
          </div>
        </div>
      )}

      <div
        style={{
          ...adminMenuWrapper,
          width: layout.boxWidth,
        }}
      >
        <div
 	 style={{
	    ...adminMenuColumn,
 	   gap: layout.adminMenuGap ?? 6,
 	 }}
	>
          {adminButtons.map((item) => {
            const isSubmenuOpen =
              item.action &&
              openSubmenu === item.action

            return (
              <div
                key={item.label}
                data-promo-clickable="true"
                onClick={() =>
                  handleAdminButtonClick(item)
                }
                style={{
                  ...adminButton,
                  width: layout.boxWidth,
                  height: layout.boxHeight,
                  background:
                    isSubmenuOpen
                      ? 'rgba(16,124,107,0.30)'
                      : 'rgba(16,124,107,0.06)',
                  boxShadow:
                    isSubmenuOpen
                      ? '0 6px 16px rgba(0,0,0,0.35)'
                      : '0 4px 12px rgba(0,0,0,0.25)',
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background =
                    'rgba(16,124,107,0.30)'

                  event.currentTarget.style.boxShadow =
                    '0 6px 16px rgba(0,0,0,0.35)'
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background =
                    isSubmenuOpen
                      ? 'rgba(16,124,107,0.30)'
                      : 'rgba(16,124,107,0.06)'

                  event.currentTarget.style.boxShadow =
                    isSubmenuOpen
                      ? '0 6px 16px rgba(0,0,0,0.35)'
                      : '0 4px 12px rgba(0,0,0,0.25)'

                  event.currentTarget.style.transform =
                    'scale(1)'
                }}
                onMouseDown={(event) => {
                  event.currentTarget.style.transform =
                    'scale(0.96)'

                  event.currentTarget.style.boxShadow =
                    '0 2px 6px rgba(0,0,0,0.2)'
                }}
                onMouseUp={(event) => {
                  event.currentTarget.style.transform =
                    'scale(1)'

                  event.currentTarget.style.boxShadow =
                    '0 6px 16px rgba(0,0,0,0.35)'
                }}
              >
                <div style={adminButtonContent}>
                  <img
                    src={item.icon}
                    alt=""
                    style={{
                      width:
                        layout.iconSize,
                      height:
                        layout.iconSize,
                      objectFit:
                        'contain',
                      filter:
                        item.iconFilter ||
                        'none',
                    }}
                  />

                  <h3
                    style={{
                      ...adminButtonTitle,
                      fontSize:
                        layout.fontSize,
                    }}
                  >
                    {item.label}
                  </h3>
                </div>
              </div>
            )
          })}
        </div>

        {openSubmenu === 'SYSTEM_SETTINGS' && (
          <>
            {renderSubmenuConnector(0)}

            <div
              style={{
                ...submenuOverlay,
                left:
                  layout.boxWidth + 12,
                top: submenuTop(0),
              }}
            >
              {systemSettingsButtons.map(
                (item) =>
                  renderSubmenuButton({
                    key: item.label,
                    label: item.label,
                    onClick: () =>
                      handleSystemSettingsClick(
                        item
                      ),
                  })
              )}

              {renderSubmenuButton({
                key:
                  'landing-page-lock',
                label:
                  landingPageLockLoading
                    ? 'Načítavam stav...'
                    : landingPageLocked
                      ? 'Odblokovať stránku'
                      : 'Zablokovať stránku',
                disabled:
                  landingPageLockLoading,
                lightRed:
                  landingPageLocked,
                tooltipText:
                  landingPageLocked
                    ? 'Vypne heslovanie prístupu na stránku'
                    : 'Zapne heslovanie prístupu na stránku',
                onClick:
                  toggleLandingPageLock,
              })}
            </div>
          </>
        )}

        {openSubmenu ===
          'NOTIFICATION_SCRIPTS' && (
          <>
            {renderSubmenuConnector(1)}

            <div
              style={{
                ...submenuOverlay,
                left:
                  layout.boxWidth + 12,
                top: submenuTop(1),
              }}
            >
              {notificationScriptButtons.map(
                (item) =>
                  renderSubmenuButton({
                    key: item.script,
                    label:
                      runningScript ===
                      item.script
                        ? `${item.label}...`
                        : item.label,
                    tooltipText:
                      item.tooltip,
                    disabled:
                      runningScript ===
                      item.script,
                    onClick: () =>
                      runNotificationScript(
                        item
                      ),
                  })
              )}
            </div>
          </>
        )}

        {openSubmenu ===
          'ADMIN_SCRIPTS' && (
          <>
            {renderSubmenuConnector(2)}

            <div
              style={{
                ...submenuOverlay,
                left:
                  layout.boxWidth + 12,
                top: submenuTop(2),
              }}
            >
              {adminScriptButtons.map(
                (item) =>
                  renderSubmenuButton({
                    key: item.script,
                    label:
                      runningScript ===
                      item.script
                        ? `${item.label}...`
                        : item.label,
                    tooltipText:
                      item.tooltip,
                    disabled:
                      runningScript ===
                      item.script,
                    lightRed: true,
                    onClick: () =>
                      runAdminScript(item),
                  })
              )}
            </div>
          </>
        )}

        {openSubmenu ===
          'TEST_SCRIPTS' && (
          <>
            {renderSubmenuConnector(3)}

            <div
              style={{
                ...submenuOverlay,
                left:
                  layout.boxWidth + 12,
                top: submenuTop(3),
              }}
            >
              {testScriptButtons.map(
                (item) =>
                  renderSubmenuButton({
                    key: item.script,
                    label:
                      runningScript ===
                      item.script
                        ? `${item.label}...`
                        : item.label,
                    tooltipText:
                      item.tooltip,
                    disabled:
                      runningScript ===
                      item.script,
                    lightRed:
                      item.script ===
                      'simulateWebhook',
                    onClick: () =>
                      runTestScript(item),
                  })
              )}
            </div>
          </>
        )}
      </div>

      {tooltip && (
        <div
          style={{
            ...tooltipStyle,
            top:
              tooltipPosition.y - 50,
            left:
              tooltipPosition.x,
          }}
        >
          {tooltip}
        </div>
      )}
    </>
  )
}

export default AdminMenu

const adminMenuWrapper = {
  position: 'relative',
  flexShrink: 0,
}

const adminMenuColumn = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const submenuConnector = {
  position: 'absolute',
  width: 12,
  borderTop:
    '1px solid rgba(16,124,107,0.65)',
  transform:
    'translateY(-50%)',
  zIndex: 4999,
  pointerEvents: 'none',
}

const submenuOverlay = {
  position: 'absolute',
  zIndex: 5000,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 8,
  background: '#c9f5da',
  border: '1.5px solid #107c6b',
  borderRadius: 10,
  boxShadow:
    '0 8px 24px rgba(0,0,0,0.35)',
}

const adminButton = {
  background:
    'rgba(16,124,107,0.06)',
  border:
    '1.5px solid #107c6b',
  borderRadius: 10,
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow:
    '0 4px 4px rgba(0,0,0,0.20)',
  transition:
    'transform 0.05s ease, box-shadow 0.1s ease',
  boxSizing: 'border-box',
}

const adminButtonContent = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
}

const adminButtonTitle = {
  margin: 0,
  color: '#0f3f39',
  whiteSpace: 'nowrap',
}

const submenuButton = {
  padding: '4px 8px',
  background: '#ededed',
  border:
    '1.5px solid #107c6b',
  borderRadius: 10,
  color: '#0f3f39',
  fontWeight: 700,
  fontFamily: 'inherit',
  boxShadow:
    '0 4px 4px rgba(0,0,0,0.20)',
  transition:
    'transform 0.05s ease, box-shadow 0.1s ease',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
}

const tooltipStyle = {
  position: 'fixed',
  transform:
    'translateX(-50%)',
  background:
    'rgba(0,0,0,0.85)',
  color: '#d9ffe4',
  padding:
    '6px 14px 8px 14px',
  borderRadius: 12,
  fontSize: 16,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 10000,
  border:
    '1.5px solid #107c6b',
  boxShadow:
    '0 12px 28px rgba(0,0,0,0.65)',
  display: 'flex',
  alignItems: 'center',
  lineHeight: 1,
}

const messageWrapper = {
  position: 'fixed',
  top: 110,
  left: '50%',
  transform:
    'translateX(-50%)',
  zIndex: 11000,
  minWidth: 320,
  maxWidth:
    'calc(100vw - 40px)',
}

const scriptResultOverlay = {
  position: 'fixed',
  inset: 0,
  zIndex: 20000,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  boxSizing: 'border-box',
}

const scriptResultModal = {
  position: 'relative',
  width: 'min(900px, 90vw)',
  maxHeight: '80vh',
  overflow: 'auto',
  background: '#ededed',
  border: '1.5px solid #107c6b',
  borderRadius: 10,
  boxShadow:
    '0 12px 40px rgba(0,0,0,0.45)',
  padding: '42px 20px 20px',
  boxSizing: 'border-box',
}

const scriptResultCloseButton = {
  position: 'absolute',
  top: 6,
  right: 10,
  width: 32,
  height: 32,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: '#0f3f39',
  fontSize: 28,
  fontWeight: 700,
  lineHeight: '32px',
  cursor: 'pointer',
}